/**
 * Guest allowance + the guest error vocabulary (SCRUM-273/274) — pure types
 * and parsers for the shared contract (docs/superpowers/plans/2026-10-07-guest-chat.md,
 * "HTTP API", "SSE"). No imports: `api-client.ts`, `pointer.ts` and the chat
 * hooks all read these, and a module that imports nothing can never sit in a
 * cycle.
 */

export type GuestAllowanceState = "ok" | "warn" | "exhausted";

export type GuestLimitReason = "messages" | "tokens" | "budget";

/** Contract `GuestAllowance`. Token numbers are never sent to clients. */
export interface GuestAllowance {
  messages_used: number;
  max_messages: number;
  remaining: number;
  soft_mode: boolean;
  state: GuestAllowanceState;
  show_prompt: boolean;
  /**
   * Contract amendment C1: why the allowance is exhausted (null unless
   * `state === "exhausted"`; the server reports the first cause in the order
   * budget → tokens → messages).
   */
  reason: GuestLimitReason | null;
}

/** Every guest-related error code the contract names. */
export const GUEST_ERROR_CODES = [
  "guest.disabled",
  "guest.ip_cap",
  "guest.unavailable",
  "guest.attachments_locked",
  "guest.limit_reached",
  "guest.claimed",
  "guest.no_session",
  "guest.claim.already_claimed",
  // Contract amendment B-10: the claiming member holds admin-console access.
  "guest.claim.not_allowed",
  "auth.registration_required",
] as const;
export type GuestErrorCode = (typeof GUEST_ERROR_CODES)[number];

/** A parsed refusal body. */
export interface GuestRefusal {
  code: GuestErrorCode;
  allowance: GuestAllowance | null;
  reason: GuestLimitReason | null;
  retryAfterSeconds: number | null;
}

/** Client-side codes the chat stream reports for guest outcomes. */
export const GUEST_STREAM_ERROR_CODES = [
  "guest_limit_reached",
  "guest_paused",
  "guest_unavailable",
  "guest_disabled",
  "guest_attachments_locked",
  "guest_claimed",
  "guest_ip_cap",
  "guest_session_expired",
] as const;
export type GuestStreamErrorCode = (typeof GUEST_STREAM_ERROR_CODES)[number];

/**
 * Why the composer has become the sign-up card. `guest.claimed` is not one:
 * the conversation lives in an account now, so the tab re-bootstraps instead
 * (W2.4c, contract amendment B-2).
 */
export type GuestStopCause = "messages" | "tokens" | "budget" | "disabled";

/**
 * What the sign-up sheet is open for. `voice`: the microphone was refused
 * with 403 `auth.registration_required` because guest voice is switched off
 * server-side (contract amendment B-7).
 */
export type GuestGate =
  | { reason: "limit"; cause: GuestStopCause; hard: boolean }
  | { reason: "attachments" }
  | { reason: "history" }
  | { reason: "voice" };

const ERROR_CODES: ReadonlySet<string> = new Set(GUEST_ERROR_CODES);
const STREAM_CODES: ReadonlySet<string> = new Set(GUEST_STREAM_ERROR_CODES);

export function isGuestErrorCode(code: string): code is GuestErrorCode {
  return ERROR_CODES.has(code);
}

export function isGuestStreamErrorCode(code: string): code is GuestStreamErrorCode {
  return STREAM_CODES.has(code);
}

/**
 * Was this an `ApiError`-shaped 403 `auth.registration_required` — a guest
 * feature (voice, attachments) switched off server-side after its config
 * was read (amendment B-7)? `useTranscribe` and `useAttachmentUpload` both
 * make exactly this check before treating the 403 as "ask the guest to sign
 * in" rather than a failure (fix round 1, DRY).
 *
 * Duck-typed rather than `err instanceof ApiError`: this module imports
 * nothing (see the header comment) so it can never sit in an import cycle
 * with `api-client.ts`, the only place `ApiError` is defined — importing it
 * here would create exactly the cycle the header comment rules out.
 */
export function isRegistrationRequired(err: unknown): boolean {
  if (typeof err !== "object" || err === null) return false;
  const e = err as { status?: unknown; code?: unknown };
  return e.status === 403 && e.code === "auth.registration_required";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isCount(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0;
}

function parseReason(value: unknown): GuestLimitReason | null {
  return value === "messages" || value === "tokens" || value === "budget" ? value : null;
}

/**
 * The contract shape, or null for anything else (never a partial object). An
 * absent or unknown `reason` reads as null rather than rejecting the whole
 * allowance — the counts and state are what the gate needs most.
 */
export function parseGuestAllowance(raw: unknown): GuestAllowance | null {
  if (!isRecord(raw)) return null;
  const used = raw["messages_used"];
  const max = raw["max_messages"];
  const remaining = raw["remaining"];
  const soft = raw["soft_mode"];
  const state = raw["state"];
  const prompt = raw["show_prompt"];
  if (!isCount(used) || !isCount(max) || !isCount(remaining)) return null;
  if (typeof soft !== "boolean" || typeof prompt !== "boolean") return null;
  if (state !== "ok" && state !== "warn" && state !== "exhausted") return null;
  return {
    messages_used: used,
    max_messages: max,
    remaining,
    soft_mode: soft,
    state,
    show_prompt: prompt,
    reason: parseReason(raw["reason"]),
  };
}

/**
 * A guest refusal from either shape the API uses: the contract's nested
 * `{"detail": {code, …}}` (guest codes) or a top-level problem body
 * (`auth.registration_required`). Null when the code is not a guest code.
 */
export function parseGuestRefusal(body: unknown): GuestRefusal | null {
  if (!isRecord(body)) return null;
  const source = isRecord(body["detail"]) ? body["detail"] : body;
  const code = source["code"];
  if (typeof code !== "string" || !isGuestErrorCode(code)) return null;
  const retry = source["retry_after_seconds"];
  return {
    code,
    allowance: parseGuestAllowance(source["allowance"]),
    reason: parseReason(source["reason"]),
    retryAfterSeconds:
      typeof retry === "number" && Number.isFinite(retry) && retry >= 0 ? retry : null,
  };
}

/** Reads a raw `Response` (the SSE path cannot use apiFetch) without consuming it. */
export async function readGuestRefusal(res: Response): Promise<GuestRefusal | null> {
  try {
    return parseGuestRefusal(await res.clone().json());
  } catch {
    return null;
  }
}

/** The stream's client code for a refusal. */
export function guestStreamErrorCode(refusal: GuestRefusal): GuestStreamErrorCode {
  switch (refusal.code) {
    case "guest.limit_reached":
      return "guest_limit_reached";
    case "guest.unavailable":
      return refusal.reason === "budget" ? "guest_paused" : "guest_unavailable";
    case "guest.disabled":
      return "guest_disabled";
    case "guest.attachments_locked":
      return "guest_attachments_locked";
    case "guest.claimed":
      return "guest_claimed";
    case "guest.ip_cap":
      return "guest_ip_cap";
    default:
      // no_session / already_claimed / registration_required never come from
      // the stream; treat a surprise as transient rather than a hard stop.
      return "guest_unavailable";
  }
}

/**
 * Stop-cold refusals become the sign-up card; transient ones return null, and
 * so does `guest_claimed` (a re-bootstrap, not a stop — B-2).
 */
export function stopCauseFor(
  code: GuestStreamErrorCode,
  reason: GuestLimitReason | null,
): GuestStopCause | null {
  switch (code) {
    case "guest_limit_reached":
      return reason === "tokens" ? "tokens" : "messages";
    case "guest_paused":
      return "budget";
    case "guest_disabled":
      return "disabled";
    default:
      return null;
  }
}

/**
 * Which limit an exhausted allowance hit — straight from the contract's
 * `reason` (amendment C1), never inferred from the counts. A null reason on an
 * exhausted allowance breaks the contract; it gets the generic "free guest
 * limit used up" copy ("tokens"), which is true of every cause.
 */
export function stopCauseForAllowance(allowance: GuestAllowance): GuestStopCause {
  return allowance.reason ?? "tokens";
}
